/**
 * CÓMO ACABÓ CADA DICTADO: `voice_audit_logs.final_result` (anexo B §3.3, F0).
 *
 * Hasta hoy 0 de 753 dictados tenían resultado: la auditoría del 23-09-2026 tuvo
 * que reconstruir a mano, por tiempos, qué se confirmó, qué se abandonó y qué se
 * volvió a dictar, y qué tocó la persona en la tarjeta. Sin eso no se puede medir
 * si un default se acepta (DV1: se apaga solo por encima del 30 % de
 * correcciones), ni aprender el léxico de lo que la gente corrige.
 *
 * Aquí, sin React: qué se toca en la tarjeta (`user_touched`), qué llegó puesto
 * (`defaults`: los de la tarjeta, voiceCardDefaults, y los del motor, `autofill`),
 * cuáles de esos se cambiaron (`defaults_overridden`) y cómo se envía.
 *
 * NUNCA bloquea ni rompe el flujo: se manda sin esperar y cualquier error se
 * traga (un registro que falla no puede impedir crear una tarea).
 */

/** Resultado de un dictado. `error` queda para el parte y usos futuros. */
export type VoiceOutcome = "confirmed" | "cancelled" | "redictated" | "error";

export const VOICE_OUTCOME_VERSION = 1;

/** Lo que se lee de una tarjeta (VoiceTaskData lo cumple; aquí sin importar React). */
export interface OutcomeCard {
  title?: string;
  title_customized?: boolean;
  type?: string;
  priority?: string;
  location_id?: string | null;
  greenhouse_id?: string | null;
  block_ids?: string[];
  pest_id?: string | null;
  treatment_kind?: "configured" | "custom";
  treatment_id?: string | null;
  assigned_to?: string | null;
  scheduled_date?: string;
  scheduled_time?: string;
  water_volume?: number | null;
  water_volume_unit?: string | null;
  resolved_products?: Array<{ product_id: string; rate_value?: number | null; rate_unit?: string | null; unit?: string }>;
  parsed_products?: Array<{ product_id?: string | null; dose_source?: string | null }>;
  user_touched?: string[];
  card_defaults?: Record<string, unknown>;
  autofill?: Record<string, { source?: string | null; n?: number | null; share?: number | null } | null | undefined>;
  /** Origen de cada valor según el motor (VoiceTaskOut.sources): campo → ValueSource. */
  sources?: Record<string, string | null | undefined> | null;
}

/* ── Claves (las mismas que lee el SQL de voice_habits) ──────────────────── */

/** Clave de la dosis de UN producto. Por id, no por posición: quitar otro producto no la mueve. */
export const productRateKey = (productId: string) => `products[${productId}].rate_value`;
export const productUnitKey = (productId: string) => `products[${productId}].rate_unit`;

/**
 * Marca campos como tocados por la persona. Se llama desde los editores de la
 * tarjeta, que son los únicos que cambian algo porque alguien lo pidió; los
 * efectos (recorte de alcance, nave ↔ sectores, título automático) NO.
 */
export function touch<T extends { user_touched?: string[] }>(task: T, ...keys: string[]): T {
  const prev = task.user_touched ?? [];
  const nuevos = keys.filter((k) => k && !prev.includes(k));
  if (nuevos.length === 0) return task;
  return { ...task, user_touched: [...prev, ...nuevos] };
}

/** Campo del motor (VoiceField de _shared/voice/types.ts) → clave de la tarjeta. */
const AUTOFILL_TO_KEY: Record<string, string> = {
  worker: "assigned_to",
  time: "time",
  date: "date",
  water: "water",
  treatment: "treatment_id",
  location: "location_id",
  greenhouse: "greenhouse_id",
  block: "block_ids",
  pest: "pest_id",
  priority: "priority",
  type: "type",
  title: "title",
};

/**
 * Orígenes del motor (ValueSource, _shared/voice/types.ts) que significan «lo DIJO
 * la persona»: lo dicho resuelto de forma única, su alias aprendido, o una lectura
 * determinista de lo dicho (ordinal, línea relativa, número partido). Todo lo
 * demás llegó puesto: `llm` (pista de la IA sin literal que la cubra — si se
 * aceptara como dicho, la IA aprendería de sí misma), `habitual`, `sugerida`,
 * `heredado`, `defecto`, `franja`, `sector_unico`…
 */
const SAID_SOURCES = new Set(["dicho", "alias", "ordinal", "relativo", "partido"]);
/** En estructura, «único en el alcance / en la empresa» es lo dicho resuelto (la nave 4 única). */
const STRUCTURE_KEYS = new Set(["location_id", "greenhouse_id", "block_ids"]);

function sourceIsDefault(key: string, source: string): boolean {
  if (SAID_SOURCES.has(source)) return false;
  if ((source === "unico_alcance" || source === "unico_empresa") && STRUCTURE_KEYS.has(key)) return false;
  return true;
}

/**
 * Lo que llegó puesto sin que se dijera, con su origen: «tarjeta» para lo que
 * rellena voiceCardDefaults; el `source` del motor para su `autofill` (la dosis,
 * una clave por producto con `dose_source`).
 *
 * También lee `sources` (VoiceTaskOut): un valor que el motor deja con origen
 * «defecto», «franja», «heredado», «llm» o el trabajador «único del alcance» es
 * un default AUNQUE no venga en `autofill`. Si no, final_result lo daría por
 * elegido por la persona y voice_habits lo aprendería: la IA aprendiendo de sí
 * misma por un olvido en el contrato con pipeline.ts (revisión 23-09).
 */
export function cardDefaults(card: OutcomeCard): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [field, source] of Object.entries(card.sources ?? {})) {
    if (!source || field === "dose") continue; // la dosis va por producto, abajo
    const key = AUTOFILL_TO_KEY[field];
    if (key && sourceIsDefault(key, String(source))) out[key] = String(source);
  }
  for (const [field, m] of Object.entries(card.autofill ?? {})) {
    if (!m) continue;
    if (field === "dose") continue; // va por producto, abajo
    const key = AUTOFILL_TO_KEY[field];
    if (key) out[key] = String(m.source || "habitual");
  }
  for (const p of card.parsed_products ?? []) {
    if (p?.product_id && p.dose_source && sourceIsDefault("dose", String(p.dose_source))) out[productRateKey(p.product_id)] = p.dose_source;
  }
  for (const key of Object.keys(card.card_defaults ?? {})) out[key] = "tarjeta";
  return out;
}

/** El valor de una clave en una tarjeta, comparable entre la inicial y la final. */
export function valueAt(card: OutcomeCard, key: string): unknown {
  const prod = key.match(/^products\[(.+)\]\.(rate_value|rate_unit)$/);
  if (prod) {
    const p = (card.resolved_products ?? []).find((x) => x.product_id === prod[1]);
    if (!p) return null;
    return prod[2] === "rate_value" ? (p.rate_value ?? null) : (p.rate_unit || p.unit || null);
  }
  switch (key) {
    case "time": return card.scheduled_time || null;
    case "date": return card.scheduled_date || null;
    case "water": return card.water_volume == null ? null : [card.water_volume, card.water_volume_unit || "L"];
    case "block_ids": return [...(card.block_ids ?? [])].sort();
    case "assigned_to": return card.assigned_to ?? null;
    case "greenhouse_id": return card.greenhouse_id ?? null;
    case "location_id": return card.location_id ?? null;
    case "treatment_id": return card.treatment_id ?? null;
    case "pest_id": return card.pest_id ?? null;
    case "priority": return card.priority ?? null;
    case "type": return card.type ?? null;
    case "title": return card.title ?? null;
    default: return null;
  }
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Qué default se cambió: el que la persona tocó, o cuyo valor final ya no es el
 * que se puso (lo pudo mover una cascada: otra nave, otros sectores). Para lo de
 * la tarjeta, el valor puesto es el anotado en `card_defaults`; para lo del motor,
 * el de la tarjeta tal como llegó (`initial`).
 */
export function overriddenDefaults(initial: OutcomeCard | null | undefined, final: OutcomeCard): string[] {
  const defaults = cardDefaults(final);
  const touched = new Set(final.user_touched ?? []);
  const out: string[] = [];
  for (const [key, source] of Object.entries(defaults)) {
    const puesto =
      source === "tarjeta"
        ? (key === "block_ids" ? [...((final.card_defaults?.[key] as string[]) ?? [])].sort() : final.card_defaults?.[key] ?? null)
        : valueAt(initial ?? final, key);
    if (touched.has(key) || !same(valueAt(final, key), puesto)) out.push(key);
  }
  return out;
}

export interface OutcomeCardFinal {
  greenhouse_id: string | null;
  location_id: string | null;
  block_ids: string[];
  assigned_to: string | null;
  date: string | null;
  time: string | null;
  type: string | null;
  priority: string | null;
  treatment_kind: "configured" | "custom" | null;
  treatment_id: string | null;
  pest_id: string | null;
  products: Array<{ product_id: string; rate_value: number | null; rate_unit: string | null }>;
  water: [number, string] | null;
  title: string | null;
  title_customized: boolean;
}

/**
 * Plaga y receta TAL COMO LAS GUARDA el insert de VoiceTaskPanel (handleConfirm)
 * y tal como las registra final_result (`cardFinal`): UNA sola regla. Hoy, sin
 * plaga en «a medida» ni fuera de un tratamiento (decisión del 14-09, taskTitle.ts)
 * y receta solo en tratamientos. Cuando DV2 guarde también la plaga dicha en «a
 * medida», se cambia AQUÍ y lo ven los dos; con dos copias, final_result diría
 * «sin plaga» en tareas que sí la llevan (revisión 23-09).
 */
export function savedTaskRefs(
  card: Pick<OutcomeCard, "type" | "treatment_kind" | "pest_id" | "treatment_id">,
): { pest_id: string | null; treatment_id: string | null } {
  const isTreatment = card.type === "treatment";
  return {
    pest_id: isTreatment && card.treatment_kind !== "custom" ? (card.pest_id || null) : null,
    treatment_id: isTreatment ? (card.treatment_id || null) : null,
  };
}

/**
 * La tarjeta tal como se guardó. Misma regla que el insert de VoiceTaskPanel
 * (`savedTaskRefs`), y el título que se escribió (el automático o el tecleado).
 */
export function cardFinal(card: OutcomeCard, savedTitle?: string | null): OutcomeCardFinal {
  const isTreatment = card.type === "treatment";
  const refs = savedTaskRefs(card);
  return {
    greenhouse_id: card.greenhouse_id ?? null,
    location_id: card.location_id ?? null,
    block_ids: [...(card.block_ids ?? [])],
    assigned_to: card.assigned_to ?? null,
    date: card.scheduled_date || null,
    time: card.scheduled_time || null,
    type: card.type ?? null,
    priority: card.priority ?? null,
    treatment_kind: isTreatment ? (card.treatment_kind ?? "configured") : null,
    treatment_id: refs.treatment_id,
    pest_id: refs.pest_id,
    products: (card.resolved_products ?? []).map((p) => ({
      product_id: p.product_id,
      rate_value: p.rate_value ?? null,
      rate_unit: p.rate_unit || p.unit || null,
    })),
    water: card.water_volume == null ? null : [card.water_volume, card.water_volume_unit || "L"],
    title: savedTitle ?? card.title ?? null,
    title_customized: !!card.title_customized,
  };
}

/* ── Sesión: un dictado mostrado, un resultado ───────────────────────────── */

export interface OutcomeSession {
  voiceLogId: string;
  /** Las tarjetas tal como llegaron (después de los defaults del panel). */
  initialCards: OutcomeCard[];
  /** Cuándo se enseñaron (ms). */
  shownAt: number;
  sent: boolean;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Abre la sesión de un dictado. Sin `voice_log_id` válido (el edge todavía no lo
 * devuelve) no hay sesión y no se envía nada.
 */
export function openOutcomeSession(voiceLogId: unknown, initialCards: OutcomeCard[], shownAt: number): OutcomeSession | null {
  if (typeof voiceLogId !== "string" || !UUID.test(voiceLogId)) return null;
  return { voiceLogId, initialCards: clone(initialCards), shownAt, sent: false };
}

/**
 * Las tarjetas ya están delante de la persona: desde aquí cuentan el tiempo hasta
 * confirmar y el «cómo llegaron» con el que se mide qué default se cambió.
 */
export function showOutcomeCards(session: OutcomeSession | null | undefined, cards: OutcomeCard[], shownAt: number): void {
  if (!session || session.sent) return;
  session.initialCards = clone(cards);
  session.shownAt = shownAt;
}

function clone<T>(x: T): T {
  try {
    return JSON.parse(JSON.stringify(x)) as T;
  } catch {
    return x;
  }
}

export interface OutcomePayload {
  voice_log_id: string;
  outcome: VoiceOutcome;
  ms_to_confirm: number | null;
  task_ids: string[];
  cards: Array<{
    k: number;
    final: OutcomeCardFinal;
    user_touched: string[];
    defaults_overridden: string[];
    defaults: Record<string, string>;
  }>;
}

/**
 * Cierra la sesión UNA vez y devuelve lo que hay que enviar (null si no hay
 * sesión o ya se envió). `cards` = como están ahora; `titles[k]` = el título
 * que se guardó; `taskIds[k]` = la tarea insertada de la tarjeta k.
 */
export function closeOutcomeSession(
  session: OutcomeSession | null | undefined,
  outcome: VoiceOutcome,
  opts: { cards?: OutcomeCard[]; titles?: Array<string | null | undefined>; taskIds?: string[]; now: number },
): OutcomePayload | null {
  if (!session || session.sent) return null;
  session.sent = true;
  const cards = opts.cards ?? [];
  return {
    voice_log_id: session.voiceLogId,
    outcome,
    ms_to_confirm: outcome === "confirmed" ? Math.max(0, Math.round(opts.now - session.shownAt)) : null,
    task_ids: outcome === "confirmed" ? (opts.taskIds ?? []).filter((id) => typeof id === "string") : [],
    cards: cards.map((card, k) => ({
      k,
      final: cardFinal(card, opts.titles?.[k]),
      user_touched: [...(card.user_touched ?? [])],
      defaults_overridden: overriddenDefaults(session.initialCards[k], card),
      defaults: cardDefaults(card),
    })),
  };
}

/* ── Re-dictado después de «empezar de nuevo» o de cerrar ────────────────── */

/**
 * Ventana del re-dictado: la MISMA regla que usó la auditoría del 23-09 para
 * emparejar dictados (el siguiente del mismo usuario a menos de 10 min del
 * anterior = re-dictado; los reales fueron de 14 s a 297 s).
 */
export const REDICTATION_WINDOW_MS = 10 * 60 * 1000;

/** Un dictado que se cerró como «cancelled», con cuándo se enseñó su tarjeta. */
export interface CancelledDictation {
  payload: OutcomePayload;
  shownAt: number;
}

/**
 * Desde el resumen no hay forma de volver a grabar sin pasar por «empezar de
 * nuevo» o cerrar, que mandan `cancelled` en el acto (para no perder el registro
 * si la app se cierra). Si después se graba OTRO dictado dentro de la ventana, lo
 * que pasó de verdad es que se volvió a dictar: 83f689c1 (rn591) se abandonó y a
 * los 63 s llegó 2bc49c37 (rn592) con las dosis que faltaban. Devuelve el mismo
 * registro con `redictated` (el edge admite cancelled → redictated), o null.
 */
export function redictationUpgrade(
  last: CancelledDictation | null | undefined,
  now: number,
  windowMs = REDICTATION_WINDOW_MS,
): OutcomePayload | null {
  if (!last || last.payload?.outcome !== "cancelled") return null;
  if (!(now - last.shownAt <= windowMs)) return null;
  return { ...last.payload, outcome: "redictated", ms_to_confirm: null, task_ids: [] };
}

/**
 * Envía sin esperar y sin fallar nunca. `send` lo pone el componente
 * (supabase.functions.invoke): así esto se prueba sin red.
 */
export function sendVoiceOutcome(send: (body: OutcomePayload) => Promise<unknown>, payload: OutcomePayload | null): void {
  if (!payload) return;
  try {
    Promise.resolve(send(payload)).catch((err) => console.warn("voice-log-outcome:", err));
  } catch (err) {
    console.warn("voice-log-outcome:", err);
  }
}
